/**
 * Chart colors, read from the active theme's CSS variables.
 *
 * Chart.js paints into a canvas, so it cannot inherit `var(--…)` the way the
 * surrounding DOM does — the values are resolved here at render time instead,
 * and re-read whenever the theme class on `<body>` changes.
 */
import { IntelligenceModels } from '@models/intelligence.model';
import Theme = IntelligenceModels.Charts.Theme;

/** Categorical palette for chart series, in draw order. */
const SERIES_COLOR_VARIABLES: readonly string[] = [
  '--color-blue-500',
  '--color-secondary-green-500',
  '--color-amber-500',
  '--color-pink-500',
  '--color-violet-500',
  '--color-teal-500',
  '--color-red-500',
];

const THEME_CLASS = 'dark-theme';

/** Opacity of a bar's fill; its border is drawn at full strength. */
const BAR_FILL_ALPHA = 0.7;

export function readChartTheme(): Theme {
  const styles = getComputedStyle(document.body);
  const cssVar = (name: string): string => styles.getPropertyValue(name).trim();

  return {
    series: SERIES_COLOR_VARIABLES.map(cssVar).filter(Boolean),
    grid: cssVar('--color-border-default'),
    text: cssVar('--color-text-subtle'),
    tooltipBackground: cssVar('--color-elevation-surface-overlay-default'),
    tooltipText: cssVar('--color-text-default'),
    tooltipBorder: cssVar('--color-border-default'),
  };
}

/** The Nth series color, wrapping around when there are more series than colors. */
export function seriesColor(theme: Theme, index: number): string {
  if (!theme.series.length) return theme.text;
  return theme.series[index % theme.series.length];
}

/** The Nth series color at the bar-fill opacity. */
export function seriesFillColor(theme: Theme, index: number): string {
  return withAlpha(seriesColor(theme, index), BAR_FILL_ALPHA);
}

/**
 * Watch for theme switches. The theme class is toggled on `<body>`
 * (see `SidebarV2Component`), so that is what is observed.
 */
export function observeThemeChanges(onChange: () => void): MutationObserver {
  let isDark = document.body.classList.contains(THEME_CLASS);
  const observer = new MutationObserver(() => {
    const nextIsDark = document.body.classList.contains(THEME_CLASS);
    if (nextIsDark === isDark) return;
    isDark = nextIsDark;
    onChange();
  });
  observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
  return observer;
}

/** `#rrggbb` → `rgba(…)`. Any other notation is already usable and passes through. */
function withAlpha(color: string, alpha: number): string {
  const match = /^#([0-9a-f]{6})$/i.exec(color);
  if (!match) return color;
  const value = parseInt(match[1], 16);
  // eslint-disable-next-line no-bitwise
  return `rgba(${(value >> 16) & 255}, ${(value >> 8) & 255}, ${value & 255}, ${alpha})`;
}
